export type HomeBuildFocusId =
  | 'landing-pages'
  | 'sites'
  | 'slides'
  | 'vibe-code'
  | 'dig';

export type ProjectType = 'app' | 'presentation' | 'general' | 'workflow';

export interface BuildFocusOption {
  id: HomeBuildFocusId;
  label: string;
  projectType: ProjectType;
}

export const HOME_BUILD_FOCUS_OPTIONS: readonly BuildFocusOption[] = [
  { id: 'landing-pages', label: 'Landing Pages', projectType: 'app' },
  { id: 'sites', label: 'Sites', projectType: 'app' },
  { id: 'slides', label: 'Slides', projectType: 'presentation' },
  { id: 'vibe-code', label: 'Vibe Code', projectType: 'general' },
  { id: 'dig', label: 'Dig', projectType: 'general' },
] as const;

export const MAX_AGENT_QUERY_LENGTH = 8000;

export interface GenerationIntent {
  query: string;
  projectType: ProjectType;
  focus: HomeBuildFocusId;
}

/** Builds cross-origin generation path consumed by landing-editor (Stage 06 wires SSE). */
export function buildGenerationPath(intent: GenerationIntent): string {
  const params = new URLSearchParams({
    query: intent.query,
    projectType: intent.projectType,
    focus: intent.focus,
  });
  return `/chat/new?${params.toString()}`;
}

export function validateGenerationQuery(query: string): string | null {
  const trimmed = query.trim();
  if (!trimmed) {
    return 'Enter a prompt to start generation.';
  }
  if (trimmed.length > MAX_AGENT_QUERY_LENGTH) {
    return `Prompt too large (${trimmed.length} characters). Maximum is ${MAX_AGENT_QUERY_LENGTH}.`;
  }
  return null;
}

export const PENDING_GENERATION_INTENT_KEY = 'landi-canvas-pending-generation-intent';

export interface PendingGenerationIntent {
  path: string;
  query: string;
  focus: HomeBuildFocusId;
}

export function storePendingGenerationIntent(intent: PendingGenerationIntent): void {
  try {
    sessionStorage.setItem(PENDING_GENERATION_INTENT_KEY, JSON.stringify(intent));
  } catch {
    /* ignore quota / private mode */
  }
}

export function consumePendingGenerationIntent(): PendingGenerationIntent | null {
  try {
    const raw = sessionStorage.getItem(PENDING_GENERATION_INTENT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_GENERATION_INTENT_KEY);
    const parsed = JSON.parse(raw) as PendingGenerationIntent;
    if (!parsed?.query?.trim() || !parsed.focus) return null;
    return parsed;
  } catch {
    return null;
  }
}
